import {BUILDING_IMPACT_TEXTURE_KEYS, QUARTER_ZONE_BURN, QUARTER_ZONE_BURN_TOTAL_MS} from '../configs/buildingConstructionAnimation';
import {CELL_OUTLINE_TEXTURE_KEYS} from '../configs/fieldCellOutlines';
import {MELEE_TEXTURE_KEYS} from '../configs/meleeAnimation';
import {CELL_OUTLINE_RADIUS_PX, ensureCellOutlineTextures} from '../utils/effects/cellOutlineTextures';
import {ensureBuildingImpactTextures} from '../utils/effects/buildingImpactTextures';
import {ensureMeleeTextures} from '../utils/effects/meleeEffectTextures';
import {resolveEmberPose, resolveZoneBurnPose} from '../utils/effects/quarterZoneBurnPoses';

/**
 * ПРОЖИГАНИЕ ЗОНЫ КВАРТАЛОВ на поле (см. configs/buildingConstructionAnimation).
 *
 * Обычная штриховка зоны — маркер ячейки, поле пересоздает его на каждое обновление
 * стора. Спрайты прожигания живут здесь и переживают перерисовки, а пока ячейка
 * принадлежит этому модулю, поле своего маркера на ней не рисует
 * (checkIfCellOwned): новая штриховка не должна появиться раньше, чем жетон
 * долетит. Когда все остыло, ячейки возвращаются полю обычными маркерами.
 *
 * Время идет по кадрам сцены: в скрытой вкладке прожигание стоит. Анимация —
 * украшение: сбой обрывает ее и возвращает ячейки полю.
 */
export default class QuarterZoneBurn {
  /**
   * @param {Phaser.Scene} scene
   * @param {Object} options
   * @param {number} options.emberDepth — слой угольков
   * @param {number} [options.linearFilter]
   * @param {function(string): ?Object} options.getCell — ячейка поля по ключу
   * @param {function(Object, string, boolean): Phaser.GameObjects.Sprite} options.createHatch —
   *   спрайт обычной штриховки (ячейка, картинка, лежит ли под ней дорога)
   * @param {function(string[], Object): void} options.returnCells — вернуть ячейки
   *   полю: нарисовать им обычные маркеры
   */
  constructor(scene, {emberDepth, linearFilter, getCell, createHatch, returnCells} = {}) {
    this.scene = scene;
    this.emberDepth = emberDepth;
    this.linearFilter = linearFilter;
    this.getCell = getCell;
    this.createHatch = createHatch;
    this.returnCells = returnCells;
    this.cellOwners = new Map();
    this.burns = new Set();
  }

  /** Принадлежит ли ячейка прожиганию: тогда поле на ней штриховку не рисует */
  checkIfCellOwned(cellKey) {
    return this.cellOwners.has(cellKey);
  }

  /**
   * Занять ячейки, пока жетон летит. Ячейка, которая уже горит от другого квартала,
   * остается за ним
   *
   * @param {Array<{cellKey: string, spriteKey: string, isOnRoad: boolean, delayMs: number}>} cells
   * @param {function(): Object} getReturnState — состояние, по которому ячейки
   *   вернутся полю
   * @returns {?Object} прожигание: его потом запускают ignite или снимают release
   */
  hold(cells, getReturnState) {
    const freeCells = cells.filter(({cellKey}) => !this.cellOwners.has(cellKey));
    if (freeCells.length === 0) {
      return null;
    }
    const burn = {cells: freeCells, getReturnState, clock: {elapsedMs: 0}, tween: null, isLit: false, sprites: []};
    freeCells.forEach(({cellKey}) => this.cellOwners.set(cellKey, burn));
    this.burns.add(burn);
    return burn;
  }

  /**
   * Жетон упал: ячейки вспыхивают
   * @param {Object} burn
   * @param {Object} [options]
   * @param {boolean} [options.isZoneVisible] — показан ли слой зоны: если нет, жар
   *   гаснет без следа, а не остывает до штриховки
   */
  ignite(burn, {isZoneVisible = true} = {}) {
    if (!burn || !this.burns.has(burn) || burn.isLit) {
      return;
    }
    const isHidden = typeof document !== 'undefined' && document.hidden;
    const isTexturesReady = !isHidden && ensureBuildingImpactTextures(this.scene, {linearFilter: this.linearFilter})
      && ensureCellOutlineTextures(this.scene, {linearFilter: this.linearFilter})
      && ensureMeleeTextures(this.scene, {linearFilter: this.linearFilter});
    if (!isTexturesReady || !this.scene?.tweens) {
      this.release(burn);
      return;
    }

    burn.isLit = true;
    burn.isZoneVisible = isZoneVisible;
    try {
      burn.sprites = burn.cells
        .map((cell) => this.createCellSprites(cell))
        .filter(Boolean);
      this.render(burn);
      const maxDelayMs = Math.max(0, ...burn.cells.map(({delayMs}) => delayMs));
      const totalMs = QUARTER_ZONE_BURN_TOTAL_MS + maxDelayMs;
      burn.tween = this.scene.tweens.add({
        targets: burn.clock,
        elapsedMs: totalMs,
        duration: totalMs,
        ease: 'Linear',
        onUpdate: () => this.guard(burn, () => this.render(burn)),
        onComplete: () => this.release(burn),
      });
    } catch (error) {
      console.warn('[QuarterZoneBurn] не удалось запустить прожигание', error);
      this.release(burn);
    }
  }

  /** Завершить прожигание: убрать спрайты и вернуть ячейки полю */
  release(burn) {
    if (!burn || !this.burns.has(burn)) {
      return;
    }
    this.destroyBurn(burn);
    const cellKeys = burn.cells.map(({cellKey}) => cellKey);
    if (cellKeys.length > 0) {
      try {
        this.returnCells?.(cellKeys, burn.getReturnState?.());
      } catch (error) {
        console.warn('[QuarterZoneBurn] не удалось вернуть ячейки полю', error);
      }
    }
  }

  /**
   * Поле перерисовало зону: ячейки, которых в зоне больше нет (постройку отменили,
   * на ячейке встало строение), гореть не должны
   * @param {Set<string>} zoneCells
   */
  syncZone(zoneCells) {
    this.burns.forEach((burn) => {
      const lostCells = burn.cells.filter(({cellKey}) => !zoneCells.has(cellKey));
      if (lostCells.length === 0) {
        return;
      }
      lostCells.forEach(({cellKey}) => {
        this.cellOwners.delete(cellKey);
        burn.sprites
          .filter((entry) => entry.cellKey === cellKey)
          .forEach((entry) => this.destroyCellSprites(entry));
      });
      const lostKeys = new Set(lostCells.map(({cellKey}) => cellKey));
      burn.cells = burn.cells.filter(({cellKey}) => !lostKeys.has(cellKey));
      burn.sprites = burn.sprites.filter((entry) => !lostKeys.has(entry.cellKey));
      if (burn.cells.length === 0) {
        this.destroyBurn(burn);
      }
    });
  }

  /** Убрать все (разбор поля): ячейки возвращать некуда */
  clear() {
    [...this.burns].forEach((burn) => this.destroyBurn(burn));
    this.cellOwners.clear();
  }

  destroyBurn(burn) {
    this.burns.delete(burn);
    burn.cells.forEach(({cellKey}) => {
      if (this.cellOwners.get(cellKey) === burn) {
        this.cellOwners.delete(cellKey);
      }
    });
    try {
      burn.tween?.stop?.();
      this.scene.tweens?.killTweensOf?.(burn.clock);
    } catch (error) {
      console.warn('[QuarterZoneBurn] не удалось остановить твин', error);
    }
    burn.sprites.forEach((entry) => this.destroyCellSprites(entry));
    burn.sprites = [];
  }

  destroyCellSprites(entry) {
    [entry.heat, entry.outline, entry.base, entry.hot, ...entry.embers].forEach((sprite) => sprite?.destroy?.());
  }

  guard(burn, action) {
    try {
      action();
    } catch (error) {
      console.warn('[QuarterZoneBurn] прожигание оборвано из-за ошибки', error);
      this.release(burn);
    }
  }

  createCellSprites({cellKey, spriteKey, isOnRoad, delayMs}) {
    const hex = this.getCell?.(cellKey);
    if (!hex) {
      return null;
    }
    const base = this.createHatch(hex, spriteKey, isOnRoad);
    const baseAlpha = base.alpha;
    base.setAlpha(0);

    const hot = this.createHatch(hex, spriteKey, isOnRoad);
    hot.setDepth(base.depth + 0.002);
    hot.setAlpha(0);

    const cellScale = hex.size / CELL_OUTLINE_RADIUS_PX;
    const createOverlay = (textureKey, depth, scaleFactor = 1) => {
      const sprite = this.scene.add.sprite(hex.centerX, hex.centerY, textureKey);
      sprite.setDepth(depth);
      sprite.setScale(cellScale * scaleFactor);
      sprite.setAlpha(0);
      return sprite;
    };
    const heat = createOverlay(BUILDING_IMPACT_TEXTURE_KEYS.ZONE_HEAT, base.depth - 0.002);
    const outline = createOverlay(CELL_OUTLINE_TEXTURE_KEYS.GLOW, base.depth + 0.001, QUARTER_ZONE_BURN.outline.sizeFactor);

    const seed = Math.random();
    const {embers} = QUARTER_ZONE_BURN;
    const emberSprites = Array.from({length: embers.count}, (unused, index) => {
      const sprite = this.scene.add.sprite(hex.centerX, hex.centerY, MELEE_TEXTURE_KEYS.GLOW);
      sprite.setDepth(this.emberDepth);
      sprite.setAlpha(0);
      sprite.setTint(embers.tints[index % embers.tints.length]);
      sprite.baseScale = hex.size * embers.sizeFactor * 2 / (sprite.width || 1);
      return sprite;
    });

    return {cellKey, hex, delayMs, seed, base, baseAlpha, hot, heat, outline, embers: emberSprites};
  }

  render(burn) {
    burn.sprites.forEach((entry) => {
      const elapsedMs = burn.clock.elapsedMs - entry.delayMs;
      const pose = resolveZoneBurnPose(elapsedMs);
      entry.base.setAlpha(burn.isZoneVisible ? entry.baseAlpha * pose.baseShare : 0);
      entry.hot.setAlpha(pose.hatchAlpha * (burn.isZoneVisible ? 1 : 1 - pose.baseShare));
      entry.hot.setTintFill(pose.color);
      entry.heat.setAlpha(pose.heatAlpha);
      entry.heat.setTint(pose.color);
      entry.outline.setAlpha(pose.outlineAlpha);
      entry.outline.setTint(pose.color);
      entry.embers.forEach((sprite, index) => {
        const ember = resolveEmberPose(elapsedMs, index, entry.seed);
        sprite.setPosition(entry.hex.centerX + ember.offsetX * entry.hex.size, entry.hex.centerY + ember.offsetY * entry.hex.size);
        sprite.setScale(sprite.baseScale * ember.scale);
        sprite.setAlpha(ember.alpha);
      });
    });
  }
}
